"use client";

import type { FlashcardData } from "@/components/flashcard";
import type { WhiteboardSessionBoard } from "@/hooks/useWhiteboard";
import { createLogger } from "@/lib/logger";
import { whiteboardFlashcardText } from "@/lib/whiteboard/flashcardText";
import { getGeneratedFlashcardText } from "@/shared/flashcard-content";
import {
  AlertCircle,
  BookmarkPlus,
  Check,
  Eye,
  Loader2,
  Presentation,
  RotateCcw,
} from "lucide-react";
import * as React from "react";

const logger = createLogger("whiteboard-recap");

type CardSaveState = "idle" | "saving" | "saved" | "error";

export interface WhiteboardRecapGalleryProps {
  boards: WhiteboardSessionBoard[];
  targetLanguage: string;
  instructionLanguage: string;
  languageLevel?: string;
  userId?: string;
  isFlashcardSaved: (sentence: string, targetLanguage: string) => boolean;
  saveFlashcard: (
    sentence: string,
    targetLanguage: string,
    instructionLanguage: string,
    data: FlashcardData
  ) => Promise<void>;
}

export function statusMeta(board: WhiteboardSessionBoard) {
  const { status, attempts } = board.result;
  if (status === "resolved") {
    return {
      label: board.result.correct > 0 ? "Solved" : "Completed",
      className: "text-emerald-300",
      icon: Check,
    };
  }
  if (status === "attempted" || attempts > 0) {
    return { label: "Practiced", className: "text-amber-300", icon: RotateCcw };
  }
  return { label: "Seen", className: "text-white/45", icon: Eye };
}

function exerciseTypeLabel(kind: string) {
  const labels: Record<string, string> = {
    gap_fill: "Fill the gap",
    word_tiles: "Word order",
    quick_choice: "Quick choice",
    equation_balance: "Equation",
    word_match: "Word match",
  };

  return labels[kind] ?? (kind === "composed" ? null : kind.replace(/_/g, " "));
}

export function WhiteboardRecapGallery({
  boards,
  targetLanguage,
  instructionLanguage,
  languageLevel,
  userId,
  isFlashcardSaved,
  saveFlashcard,
}: WhiteboardRecapGalleryProps) {
  const [saveStates, setSaveStates] = React.useState<Record<string, CardSaveState>>({});

  const visibleBoards = React.useMemo(
    () =>
      boards.filter(
        (board) =>
          board.result.label &&
          (board.result.status === "resolved" ||
            board.result.status === "attempted" ||
            board.result.attempts > 0)
      ),
    [boards]
  );

  const canSave = Boolean(userId && targetLanguage);

  const handleSave = React.useCallback(
    async (board: WhiteboardSessionBoard) => {
      const exerciseId = board.result.exerciseId;
      const sentence = whiteboardFlashcardText(board.spec);
      if (!canSave || !sentence) return;
      if (saveStates[exerciseId] === "saving" || saveStates[exerciseId] === "saved") return;
      if (isFlashcardSaved(sentence, targetLanguage)) {
        setSaveStates((current) => ({ ...current, [exerciseId]: "saved" }));
        return;
      }

      setSaveStates((current) => ({ ...current, [exerciseId]: "saving" }));
      try {
        const response = await fetch("/api/generate-flashcard", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sentence,
            targetLanguage,
            instructionLanguage,
            languageLevel,
            userId,
          }),
        });
        if (!response.ok) {
          const errorData = (await response.json().catch(() => null)) as any;
          throw new Error(errorData?.error || "Failed to generate flashcard");
        }
        const result = (await response.json()) as { data: FlashcardData };
        await saveFlashcard(
          getGeneratedFlashcardText(result.data),
          targetLanguage,
          instructionLanguage,
          result.data
        );
        setSaveStates((current) => ({ ...current, [exerciseId]: "saved" }));
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes("already saved")) {
          setSaveStates((current) => ({ ...current, [exerciseId]: "saved" }));
          return;
        }
        logger.error("Failed to save board flashcard", { error: message, sentence });
        setSaveStates((current) => ({ ...current, [exerciseId]: "error" }));
      }
    },
    [
      canSave,
      instructionLanguage,
      isFlashcardSaved,
      languageLevel,
      saveFlashcard,
      saveStates,
      targetLanguage,
      userId,
    ]
  );

  if (visibleBoards.length === 0) return null;

  const solvedCount = visibleBoards.filter(
    (board) => board.result.status === "resolved" && board.result.correct > 0
  ).length;
  const completedCount = visibleBoards.filter(
    (board) => board.result.status === "resolved" && board.result.correct === 0
  ).length;

  return (
    <section className="overflow-hidden rounded-lg border border-white/10 bg-[#0f0f14] shadow-2xl shadow-black/40">
      <div className="flex items-start gap-3 px-4 pb-4 pr-12 pt-4 sm:px-5 sm:pb-5 sm:pr-12 sm:pt-5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-orange-500 text-white shadow-sm shadow-orange-950/30">
          <Presentation size={18} />
        </span>
        <div className="min-w-0">
          <h2 className="text-base font-bold text-white sm:text-lg">Session boards</h2>
          <p className="mt-0.5 text-sm leading-relaxed text-white/55">
            {completedCount > 0
              ? `${completedCount} completed without a score. Save the useful ones for later.`
              : solvedCount > 0
                ? `${solvedCount} of ${visibleBoards.length} solved. Save the useful ones for later.`
                : "Save the useful ones for another round."}
          </p>
        </div>
      </div>

      <div className="border-t border-white/10">
        {visibleBoards.map((board) => {
          const status = statusMeta(board);
          const StatusIcon = status.icon;
          const typeLabel = exerciseTypeLabel(board.result.kind);
          const exerciseId = board.result.exerciseId;
          const saveText = whiteboardFlashcardText(board.spec);
          const alreadySaved =
            saveStates[exerciseId] === "saved" ||
            (saveText ? isFlashcardSaved(saveText, targetLanguage) : false);
          const saveState: CardSaveState = alreadySaved
            ? "saved"
            : (saveStates[exerciseId] ?? "idle");

          return (
            <button
              key={exerciseId}
              type="button"
              onClick={() => handleSave(board)}
              disabled={!canSave || !saveText || saveState === "saving" || saveState === "saved"}
              className="group flex w-full flex-col gap-3 border-b border-white/10 bg-white/[0.025] px-4 py-4 text-left transition-colors last:border-b-0 hover:bg-white/[0.055] disabled:cursor-default disabled:hover:bg-white/[0.025] sm:flex-row sm:items-center sm:justify-between sm:px-5"
              title={
                !saveText
                  ? "No word or phrase to save"
                  : !canSave
                    ? "Sign in to save flashcards"
                    : saveState === "saved"
                      ? "Saved to your flashcards"
                      : "Save to flashcards"
              }
            >
              <div className="min-w-0">
                <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold">
                  <span className={`inline-flex items-center gap-1.5 ${status.className}`}>
                    <StatusIcon size={14} strokeWidth={2.5} />
                    {status.label}
                  </span>
                  {typeLabel ? (
                    <>
                      <span aria-hidden="true" className="text-white/20">
                        /
                      </span>
                      <span className="capitalize text-white/40">{typeLabel}</span>
                    </>
                  ) : null}
                </div>
                <div className="line-clamp-2 text-base font-semibold leading-snug text-white">
                  {saveText || board.result.label}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1.5 text-sm font-semibold sm:justify-end">
                {saveState === "saving" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-orange-400" />
                    <span className="text-white/55">Creating flashcard...</span>
                  </>
                ) : saveState === "saved" ? (
                  <>
                    <Check className="h-4 w-4 text-emerald-400" />
                    <span className="text-emerald-300">Saved to flashcards</span>
                  </>
                ) : saveState === "error" ? (
                  <>
                    <AlertCircle className="h-4 w-4 text-red-400" />
                    <span className="text-red-300">Could not save. Try again</span>
                  </>
                ) : (
                  <>
                    <BookmarkPlus className="h-4 w-4 text-orange-400 transition-transform group-hover:-translate-y-0.5" />
                    <span className="text-orange-300 group-hover:text-orange-200">
                      {!saveText
                        ? "No phrase to save"
                        : canSave
                          ? "Save to flashcards"
                          : "Sign in to save"}
                    </span>
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
